/**
 * @fileoverview Shared Account schema used by both frontend and backend.
 * Represents user accounts with authentication provider information.
 */

import { z } from 'zod';
import { commonValidators, createCrudSchemas, createEntityIdSchema, createdAtSchema, deletedAtSchema, getSchemaFields, updatedAtSchema } from './base.js';
import { AccountTypeEnum, AuthProviderEnum, ID_PATTERNS } from './types.js';

/**
 * Account validation schema.
 */
export const AccountSchema = z.object({
  id: createEntityIdSchema(ID_PATTERNS.ACCOUNT, 'ID must be in ac-prefixed nanoid7 format'),
  provider: AuthProviderEnum.default('github'),
  provider_user_id: commonValidators.requiredString('Provider User ID', 255),
  username: commonValidators.requiredString('Username', 255),
  display_name: commonValidators.optionalString(255),
  email: commonValidators.email(),
  avatar_url: commonValidators.optionalUrl('Avatar URL'),
  account_type: AccountTypeEnum.default('human'),
  metadata: commonValidators.jsonField('Metadata'),
  updated_by_account_id: commonValidators.foreignKey('Updated by account ID'),
  deleted_by_account_id: commonValidators.foreignKey('Deleted by account ID'),
  deleted_at: deletedAtSchema,
  created_at: createdAtSchema,
  updated_at: updatedAtSchema,
});

const { CreateSchema, UpdateSchema } = createCrudSchemas(AccountSchema);

export const AccountCreateSchema = CreateSchema;
export const AccountUpdateSchema = UpdateSchema;

export type Account = z.infer<typeof AccountSchema>;
export type AccountCreate = z.infer<typeof AccountCreateSchema>;
export type AccountUpdate = z.infer<typeof AccountUpdateSchema>;

export const ACCOUNT_FIELDS = getSchemaFields(AccountSchema);
